import DraftOffsetKey from 'draft-js/lib/DraftOffsetKey';
import {
    AtomicBlockUtils,
    ContentState,
    ContentBlock,
    EditorState,
    SelectionState,
    genKey,
    RichUtils,
} from 'draft-js';
import { List } from 'immutable';

import {
    BLOCK_TYPE, ENTITY_TYPE, IMMUTABLE, TAB_MAX_DEPTH,
} from './CONSTANTS';

const isBlockFocused = (readOnly, block, selection) => selection.getAnchorKey() === block.getKey()
    && selection.isCollapsed() && readOnly !== true;

const removeInlineStylesFromCurrentBlock = (editorState) => {
    const styles = editorState.getCurrentInlineStyle();
    [...styles].forEach((style) => {
        editorState = RichUtils.toggleInlineStyle(editorState, style);
    });
    return editorState;
};

const adjustBlockDepthForContentState = (contentState, selectionState, adjustment) => {
    const startKey = selectionState.getStartKey();
    const endKey = selectionState.getEndKey();
    let blockMap = contentState.getBlockMap();
    const blocks = blockMap
        .toSeq()
        .skipUntil((_, k) => k === startKey)
        .takeUntil((_, k) => k === endKey)
        .concat([[endKey, blockMap.get(endKey)]])
        .map((block) => {
            let depth = block.getDepth() + adjustment;
            depth = Math.max(0, Math.min(depth, TAB_MAX_DEPTH));
            return block.set('depth', depth);
        });

    blockMap = blockMap.merge(blocks);

    return contentState.merge({
        blockMap,
        selectionBefore: selectionState,
        selectionAfter: selectionState,
    });
};

const replaceWithBlock = (content, selection, block) => {
    const blockMap = content.getBlockMap();
    const currentBlock = content.getBlockForKey(selection.getStartKey());
    const blocksBefore = blockMap.toSeq().takeUntil(v => (v === currentBlock));
    const blocksAfter = blockMap.toSeq().skipUntil(v => (v === currentBlock)).rest();
    const newBlockKey = genKey();
    const defaultBlock = {
        type: BLOCK_TYPE.UNSTYLED,
        text: '',
        depth: 0,
        ...block,
        key: newBlockKey,
    };

    const newBlock = new ContentBlock({
        ...defaultBlock,
    });

    const newBlockMap = blocksBefore.concat(
        [[newBlockKey, newBlock]],
        blocksAfter,
    ).toOrderedMap();

    const newContent = content.merge({
        blockMap: newBlockMap,
        selectionBefore: selection,
        selectionAfter: selection.merge({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: 0,
            isBackward: false,
        }),
    });
    return newContent;
};

const insertBlockAfterContent = (content, selection, blockKey, newType, data) => {
    const blockMap = content.getBlockMap();
    const block = blockMap.get(blockKey);
    const blocksBefore = blockMap.toSeq().takeUntil(v => (v === block));
    const blocksAfter = blockMap.toSeq().skipUntil(v => (v === block)).rest();
    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: newType,
        text: '',
        characterList: block.getCharacterList().slice(0, 0),
        depth: 0,
        data,
    });
    const newBlockMap = blocksBefore.concat(
        [[blockKey, block], [newBlockKey, newBlock]],
        blocksAfter,
    ).toOrderedMap();
    const newContent = content.merge({
        blockMap: newBlockMap,
        selectionBefore: selection,
        selectionAfter: selection.merge({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: 0,
            isBackward: false,
        }),
    });
    return newContent;
};

const insertEmptyBlockAtEnd = (editorState) => {
    const content = editorState.getCurrentContent();
    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
        key: newBlockKey,
        text: '',
        type: BLOCK_TYPE.UNSTYLED,
    });
    const newBlockMap = content
        .getBlockMap()
        .set(newBlockKey, newBlock);
    const newContentState = ContentState.createFromBlockArray(newBlockMap.toArray());
    return EditorState.push(editorState, newContentState, 'insert-fragment');
};

const insertBlockAfter = (editorState, blockKey, newType, data) => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContent = insertBlockAfterContent(content, selection, blockKey, newType, data);
    return removeInlineStylesFromCurrentBlock(EditorState.push(editorState, newContent, 'split-block'));
};

const getCurrentBlock = (editorState) => {
    const selectionState = editorState.getSelection();
    const key = selectionState.getStartKey();
    const contentState = editorState.getCurrentContent();
    return contentState.getBlockForKey(key);
};

const getCurrentBlockText = editorState => getCurrentBlock(editorState).getText();

const getCurrentBlockTextUntilCursor = (editorState) => {
    const end = editorState.getSelection().getEndOffset();

    return getCurrentBlockText(editorState).slice(0, end);
};

const shouldHidePlaceholder = (editorState) => {
    const content = editorState.getCurrentContent();
    const blockType = editorState.getCurrentContent().getBlockMap().first().getType();
    return content.getPlainText() !== ''
        || blockType.includes(BLOCK_TYPE.LIST)
        || blockType.includes(BLOCK_TYPE.CODE)
        || blockType.includes(BLOCK_TYPE.HR);
};

const shouldExitList = (editorState) => {
    const blockType = RichUtils.getCurrentBlockType(editorState);
    const currentBlockText = getCurrentBlockText(editorState);
    const blockDepth = getCurrentBlock(editorState).getDepth();

    return !(blockType === BLOCK_TYPE.UNSTYLED)
        && (currentBlockText.trim() === '')
        && blockType.includes(BLOCK_TYPE.LIST)
        && blockDepth === 0;
};

const shouldUnindent = (editorState) => {
    const blockType = RichUtils.getCurrentBlockType(editorState);
    const currentBlockText = getCurrentBlockText(editorState);
    const blockDepth = getCurrentBlock(editorState).getDepth();

    return !(blockType === BLOCK_TYPE.UNSTYLED)
        && (currentBlockText.trim() === '')
        && blockType.includes(BLOCK_TYPE.LIST)
        && blockDepth > 0;
};

const insertBlockBeforeSelection = (contentState, selection, blockType) => {
    blockType = blockType || BLOCK_TYPE.UNSTYLED;

    const blockKey = selection.getAnchorKey();
    const blockMap = contentState.getBlockMap();
    const block = blockMap.get(blockKey);
    const blocksBefore = blockMap.takeWhile(v => (v !== block));
    const blocksAfter = blockMap.skipUntil(v => (v === block));

    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: blockType,
        text: '',
        characterList: List(),
        depth: 0,
    });

    const newBlockMap = blocksBefore.concat(
        [[newBlockKey, newBlock]],
        blocksAfter,
    ).toOrderedMap();

    return contentState.merge({
        blockMap: newBlockMap,
    });
};

const insertHrBlock = (editorState, contentState) => {
    let content = contentState || editorState.getCurrentContent();

    content = content.createEntity(
        ENTITY_TYPE.HR,
        IMMUTABLE,
        { },
    );

    const entityKey = content.getLastCreatedEntityKey();

    editorState = EditorState.push(editorState, content);
    return AtomicBlockUtils.insertAtomicBlock(
        editorState,
        entityKey,
        ' ',
    );
};

const containsEntity = (contentBlock, entity, start = 0, end) => {
    const text = contentBlock.getText();
    end = end || text.length;
    const entities = contentBlock.getCharacterList();
    return !!entities.slice(start, end).find(ent => ent.getEntity() === entity);
};

const containsEntities = (contentBlock, start, end) => {
    const text = contentBlock.getText();
    end = end || text.length;
    const characters = contentBlock.getCharacterList();
    return !!characters.slice(start, end).find(char => char.getEntity() != null);
};

const containsStyle = (contentBlock, style, start = 0, end) => {
    const text = contentBlock.getText();
    end = end || text.length;
    const entities = contentBlock.getCharacterList();
    return !!entities.slice(start, end).find(ent => ent.getStyle().includes(style));
};

const selectAtomicBlock = (block, setEditorState, editorState) => {
    const offsetKey = DraftOffsetKey.encode(block.getKey(), 0, 0);
    const node = document.querySelectorAll(`[data-offset-key="${offsetKey}"]`)[0];
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(node, 0);
    range.setEnd(node, 0);
    selection.removeAllRanges();
    selection.addRange(range);

    setEditorState(
        EditorState.forceSelection(
            editorState,
            new SelectionState({
                anchorKey: block.getKey(),
                anchorOffset: 0,
                focusKey: block.getKey(),
                focusOffset: 0,
                isBackward: false,
            }),
        ),
    );
};

const getBlockEntityType = (editorState, block) => {
    const contentState = editorState.getCurrentContent();
    const entityKey = block.getEntityAt(0);
    const entity = contentState.getEntity(entityKey);
    return entity.getType().toUpperCase();
};

export {
    adjustBlockDepthForContentState,
    getCurrentBlock,
    getCurrentBlockText,
    getCurrentBlockTextUntilCursor,
    getBlockEntityType,
    insertHrBlock,
    containsStyle,
    isBlockFocused,
    selectAtomicBlock,
    containsEntity,
    containsEntities,
    insertBlockAfter,
    insertBlockAfterContent,
    insertBlockBeforeSelection,
    replaceWithBlock,
    insertEmptyBlockAtEnd,
    shouldHidePlaceholder,
    shouldExitList,
    shouldUnindent,
    removeInlineStylesFromCurrentBlock,
};
